// apps/web/src/views/app-views/roulette/reports/index.js

import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'

import {
  BarChartOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  GiftOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserAddOutlined,
} from '@ant-design/icons'

import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import ReportsService from 'services/ReportsService'

const { Title, Text } = Typography
const { Option } = Select
const { RangePicker } = DatePicker

const DEFAULT_SUMMARY = {
  totalLeads: 0,
  totalParticipants: 0,
  totalSpins: 0,
  wonSpins: 0,

  deliveries: {
    delivered: 0,
    pending: 0,
    cancelled: 0,
  },

  stock: {
    initial: 0,
    available: 0,
  },

  conversionRate: 0,
}

const getStoredRole = () => {
  return localStorage.getItem('USER_ROLE') || ''
}

const normalizeArray = (value) => {
  return Array.isArray(value)
    ? value
    : []
}

const formatDateTime = (value) => {
  if (!value) {
    return '-'
  }

  const date = dayjs(value)

  if (!date.isValid()) {
    return '-'
  }

  return date.format('DD/MM/YYYY HH:mm')
}

const sanitizeFilenameDate = () => {
  return dayjs().format('YYYY-MM-DD-HH-mm')
}

const ReportsPage = () => {
  const userRole = getStoredRole()
  const isAdminNlb =
    userRole === 'ADMIN_NLB'

  const [clients, setClients] = useState([])
  const [events, setEvents] = useState([])
  const [games, setGames] = useState([])
  const [forms, setForms] = useState([])

  const [clientId, setClientId] = useState()
  const [eventId, setEventId] = useState()
  const [gameId, setGameId] = useState()
  const [formId, setFormId] = useState()

  const [period, setPeriod] = useState(null)
  const [search, setSearch] = useState('')
  const [consentLgpd, setConsentLgpd] =
    useState()

  const [summary, setSummary] =
    useState(DEFAULT_SUMMARY)

  const [leads, setLeads] = useState([])
  const [customColumns, setCustomColumns] =
    useState([])

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })

  const [loadingFilters, setLoadingFilters] =
    useState(false)

  const [loadingSummary, setLoadingSummary] =
    useState(false)

  const [loadingLeads, setLoadingLeads] =
    useState(false)

  const [exporting, setExporting] =
    useState(null)

  const [selectedLead, setSelectedLead] =
    useState(null)

  const [detailOpen, setDetailOpen] =
    useState(false)

  const filteredEvents = useMemo(() => {
    if (!clientId) {
      return events
    }

    return events.filter(
      (event) =>
        event.clientId === clientId,
    )
  }, [events, clientId])

  const filteredGames = useMemo(() => {
    return games.filter((game) => {
      if (
        eventId &&
        game.eventId !== eventId
      ) {
        return false
      }

      if (clientId) {
        const event = events.find(
          (item) =>
            item.id === game.eventId,
        )

        if (
          event &&
          event.clientId !== clientId
        ) {
          return false
        }
      }

      return true
    })
  }, [
    games,
    events,
    clientId,
    eventId,
  ])

  const filteredForms = useMemo(() => {
    return forms.filter((form) => {
      if (
        gameId &&
        form.gameId !== gameId
      ) {
        return false
      }

      if (
        !gameId &&
        eventId &&
        form.eventId !== eventId &&
        !filteredGames.some(
          (game) =>
            game.id === form.gameId,
        )
      ) {
        return false
      }

      if (clientId) {
        const formEventId =
          form.eventId ||
          games.find(
            (game) =>
              game.id === form.gameId,
          )?.eventId

        const event = events.find(
          (item) =>
            item.id === formEventId,
        )

        if (
          event &&
          event.clientId !== clientId
        ) {
          return false
        }
      }

      return true
    })
  }, [
    forms,
    games,
    events,
    filteredGames,
    clientId,
    eventId,
    gameId,
  ])

  const buildParams = (
    overrides = {},
  ) => {
    const params = {
      ...(clientId
        ? {
            clientId,
          }
        : {}),

      ...(eventId
        ? {
            eventId,
          }
        : {}),

      ...(gameId
        ? {
            gameId,
          }
        : {}),

      ...(formId
        ? {
            formId,
          }
        : {}),

      ...(Array.isArray(period) &&
      period.length === 2
        ? {
            startDate:
              period[0].format(
                'YYYY-MM-DD',
              ),

            endDate:
              period[1].format(
                'YYYY-MM-DD',
              ),
          }
        : {}),

      ...(search.trim()
        ? {
            search:
              search.trim(),
          }
        : {}),

      ...(consentLgpd !== undefined
        ? {
            consentLgpd,
          }
        : {}),

      ...overrides,
    }

    return params
  }

  const loadFilters = async (
    selectedClientId,
  ) => {
    setLoadingFilters(true)

    try {
      const response =
        await ReportsService.getFilters(
          selectedClientId,
        )

      setClients(
        normalizeArray(
          response?.clients,
        ),
      )

      setEvents(
        normalizeArray(
          response?.events,
        ),
      )

      setGames(
        normalizeArray(
          response?.games,
        ),
      )

      setForms(
        normalizeArray(
          response?.forms,
        ),
      )
    } catch (error) {
      console.error(
        'ERRO AO CARREGAR FILTROS:',
        error,
      )

      message.error(
        error?.response?.data?.message ||
          'Erro ao carregar filtros dos relatórios',
      )

      setClients([])
      setEvents([])
      setGames([])
      setForms([])
    } finally {
      setLoadingFilters(false)
    }
  }

  const loadSummary = async () => {
    setLoadingSummary(true)

    try {
      const response =
        await ReportsService.getSummary(
          buildParams(),
        )

      setSummary({
        ...DEFAULT_SUMMARY,
        ...response,

        deliveries: {
          ...DEFAULT_SUMMARY.deliveries,
          ...(response?.deliveries ||
            {}),
        },

        stock: {
          ...DEFAULT_SUMMARY.stock,
          ...(response?.stock || {}),
        },
      })
    } catch (error) {
      console.error(
        'ERRO AO CARREGAR RESUMO:',
        error,
      )

      message.error(
        error?.response?.data?.message ||
          'Erro ao carregar resumo dos relatórios',
      )

      setSummary(DEFAULT_SUMMARY)
    } finally {
      setLoadingSummary(false)
    }
  }

  const loadLeads = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
  ) => {
    setLoadingLeads(true)

    try {
      const response =
        await ReportsService.getLeads(
          buildParams({
            page,
            limit: pageSize,
          }),
        )

      setLeads(
        normalizeArray(
          response?.items,
        ),
      )

      setCustomColumns(
        normalizeArray(
          response?.customColumns,
        ),
      )

      setPagination({
        current:
          response?.pagination?.page ||
          page,

        pageSize:
          response?.pagination?.limit ||
          pageSize,

        total:
          response?.pagination?.total ||
          0,
      })
    } catch (error) {
      console.error(
        'ERRO AO CARREGAR LEADS:',
        error,
      )

      message.error(
        error?.response?.data?.message ||
          'Erro ao carregar relatório de leads',
      )

      setLeads([])
      setCustomColumns([])
    } finally {
      setLoadingLeads(false)
    }
  }

  const refreshReports = async () => {
    await Promise.all([
      loadSummary(),
      loadLeads(1, pagination.pageSize),
    ])
  }

  const clearFilters = async () => {
    setClientId(undefined)
    setEventId(undefined)
    setGameId(undefined)
    setFormId(undefined)
    setPeriod(null)
    setSearch('')
    setConsentLgpd(undefined)

    setPagination((current) => ({
      ...current,
      current: 1,
    }))
  }

  const downloadBlob = (
    blobResponse,
    filename,
    mimeType,
  ) => {
    const blob =
      blobResponse instanceof Blob
        ? blobResponse
        : new Blob(
            [blobResponse],
            {
              type: mimeType,
            },
          )

    const url =
      window.URL.createObjectURL(
        blob,
      )

    const link =
      document.createElement('a')

    link.href = url
    link.download = filename

    document.body.appendChild(link)
    link.click()
    link.remove()

    window.URL.revokeObjectURL(url)
  }

  const handleExport = async (
    type,
  ) => {
    setExporting(type)

    try {
      const params =
        buildParams()

      const timestamp =
        sanitizeFilenameDate()

      if (type === 'csv') {
        const response =
          await ReportsService.exportLeadsCsv(
            params,
          )

        downloadBlob(
          response,
          `relatorio-leads-${timestamp}.csv`,
          'text/csv;charset=utf-8',
        )
      }

      if (type === 'xlsx') {
        const response =
          await ReportsService.exportLeadsXlsx(
            params,
          )

        downloadBlob(
          response,
          `relatorio-leads-${timestamp}.xlsx`,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        )
      }

      if (type === 'pdf') {
        const response =
          await ReportsService.exportLeadsPdf(
            params,
          )

        downloadBlob(
          response,
          `relatorio-leads-${timestamp}.pdf`,
          'application/pdf',
        )
      }

      message.success(
        'Relatório exportado com sucesso',
      )
    } catch (error) {
      console.error(
        'ERRO AO EXPORTAR RELATÓRIO:',
        error,
      )

      message.error(
        error?.response?.data?.message ||
          'Erro ao exportar relatório',
      )
    } finally {
      setExporting(null)
    }
  }

  const handleClientChange = async (
    value,
  ) => {
    setClientId(value)
    setEventId(undefined)
    setGameId(undefined)
    setFormId(undefined)

    if (isAdminNlb) {
      await loadFilters(value)
    }
  }

  const handleEventChange = (
    value,
  ) => {
    setEventId(value)
    setGameId(undefined)
    setFormId(undefined)
  }

  const handleGameChange = (
    value,
  ) => {
    setGameId(value)
    setFormId(undefined)
  }

  const handleTableChange = (
    tablePagination,
  ) => {
    loadLeads(
      tablePagination.current,
      tablePagination.pageSize,
    )
  }

  const openLeadDetails = (
    lead,
  ) => {
    setSelectedLead(lead)
    setDetailOpen(true)
  }

  useEffect(() => {
    loadFilters()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (loadingFilters) {
      return
    }

    loadSummary()
    loadLeads(1, 20)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingFilters])

  const tableColumns = [
    {
      title: 'Data',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      fixed: 'left',

      render: (value) =>
        formatDateTime(value),
    },
    {
      title: 'Cliente',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 180,

      render: (value) =>
        value || '-',
    },
    {
      title: 'Evento',
      dataIndex: 'eventName',
      key: 'eventName',
      width: 190,

      render: (value) =>
        value || '-',
    },
    {
      title: 'Jogo',
      dataIndex: 'gameName',
      key: 'gameName',
      width: 190,

      render: (value) =>
        value || '-',
    },
    {
      title: 'Formulário',
      dataIndex: 'formName',
      key: 'formName',
      width: 190,

      render: (value) =>
        value || '-',
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
      width: 200,

      render: (value) => (
        <Text strong>
          {value || '-'}
        </Text>
      ),
    },
    {
      title: 'Telefone',
      dataIndex: 'telefone',
      key: 'telefone',
      width: 150,

      render: (value) =>
        value || '-',
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
      width: 230,

      render: (value) =>
        value || '-',
    },
    {
      title: 'LGPD',
      dataIndex: 'consentLgpd',
      key: 'consentLgpd',
      width: 100,
      align: 'center',

      render: (value) =>
        value ? (
          <Tag
            color="green"
            icon={
              <CheckCircleOutlined />
            }
          >
            Sim
          </Tag>
        ) : (
          <Tag color="red">
            Não
          </Tag>
        ),
    },

    ...customColumns.map(
      (column) => ({
        title: column,
        key: `custom-${column}`,
        width: 180,

        render: (_, record) =>
          record.customValues?.[
            column
          ] || '-',
      }),
    ),

    {
      title: 'Ações',
      key: 'actions',
      width: 110,
      fixed: 'right',

      render: (_, record) => (
        <Button
          size="small"
          onClick={() =>
            openLeadDetails(record)
          }
        >
          Ver dados
        </Button>
      ),
    },
  ]

  return (
    <>
      <Space
        style={{
          width: '100%',
          justifyContent:
            'space-between',
          marginBottom: 16,
        }}
        align="start"
      >
        <div>
          <Title
            level={2}
            style={{
              margin: 0,
            }}
          >
            Relatórios
          </Title>

          <p>
            Acompanhe resultados,
            leads, jogadas, entregas e
            estoque dos jogos.
          </p>
        </div>

        <Button
          icon={
            <ReloadOutlined />
          }
          loading={
            loadingSummary ||
            loadingLeads
          }
          onClick={
            refreshReports
          }
        >
          Atualizar
        </Button>
      </Space>

      <Card
        title="Filtros"
        loading={loadingFilters}
        style={{
          marginBottom: 16,
        }}
      >
        <Row gutter={[16, 16]}>
          {isAdminNlb && (
            <Col
              xs={24}
              md={12}
              lg={6}
            >
              <Text strong>
                Cliente
              </Text>

              <Select
                allowClear
                showSearch
                value={clientId}
                placeholder="Todos os clientes"
                optionFilterProp="children"
                style={{
                  width: '100%',
                  marginTop: 8,
                }}
                onChange={
                  handleClientChange
                }
              >
                {clients.map(
                  (client) => (
                    <Option
                      key={client.id}
                      value={client.id}
                    >
                      {client.name}
                    </Option>
                  ),
                )}
              </Select>
            </Col>
          )}

          <Col
            xs={24}
            md={12}
            lg={6}
          >
            <Text strong>
              Evento
            </Text>

            <Select
              allowClear
              showSearch
              value={eventId}
              placeholder="Todos os eventos"
              optionFilterProp="children"
              style={{
                width: '100%',
                marginTop: 8,
              }}
              onChange={
                handleEventChange
              }
            >
              {filteredEvents.map(
                (event) => (
                  <Option
                    key={event.id}
                    value={event.id}
                  >
                    {event.name}
                  </Option>
                ),
              )}
            </Select>
          </Col>

          <Col
            xs={24}
            md={12}
            lg={6}
          >
            <Text strong>
              Jogo
            </Text>

            <Select
              allowClear
              showSearch
              value={gameId}
              placeholder="Todos os jogos"
              optionFilterProp="children"
              style={{
                width: '100%',
                marginTop: 8,
              }}
              onChange={
                handleGameChange
              }
            >
              {filteredGames.map(
                (game) => (
                  <Option
                    key={game.id}
                    value={game.id}
                  >
                    {game.name}
                  </Option>
                ),
              )}
            </Select>
          </Col>

          <Col
            xs={24}
            md={12}
            lg={6}
          >
            <Text strong>
              Formulário
            </Text>

            <Select
              allowClear
              showSearch
              value={formId}
              placeholder="Todos os formulários"
              optionFilterProp="children"
              style={{
                width: '100%',
                marginTop: 8,
              }}
              onChange={
                setFormId
              }
            >
              {filteredForms.map(
                (form) => (
                  <Option
                    key={form.id}
                    value={form.id}
                  >
                    {form.name}
                  </Option>
                ),
              )}
            </Select>
          </Col>

          <Col
            xs={24}
            md={12}
            lg={8}
          >
            <Text strong>
              Período
            </Text>

            <RangePicker
              value={period}
              onChange={setPeriod}
              format="DD/MM/YYYY"
              style={{
                width: '100%',
                marginTop: 8,
              }}
              placeholder={[
                'Data inicial',
                'Data final',
              ]}
            />
          </Col>

          <Col
            xs={24}
            md={12}
            lg={8}
          >
            <Text strong>
              Pesquisa
            </Text>

            <Input
              allowClear
              value={search}
              prefix={
                <SearchOutlined />
              }
              placeholder="Nome, telefone, e-mail ou campo personalizado"
              style={{
                marginTop: 8,
              }}
              onChange={(event) =>
                setSearch(
                  event.target.value,
                )
              }
              onPressEnter={
                refreshReports
              }
            />
          </Col>

          <Col
            xs={24}
            md={12}
            lg={4}
          >
            <Text strong>
              Consentimento LGPD
            </Text>

            <Select
              allowClear
              value={consentLgpd}
              placeholder="Todos"
              style={{
                width: '100%',
                marginTop: 8,
              }}
              onChange={
                setConsentLgpd
              }
            >
              <Option value={true}>
                Aceitou
              </Option>

              <Option value={false}>
                Não aceitou
              </Option>
            </Select>
          </Col>

          <Col
            xs={24}
            md={12}
            lg={4}
          >
            <Text strong>
              &nbsp;
            </Text>

            <Space
              style={{
                display: 'flex',
                marginTop: 8,
              }}
            >
              <Button
                type="primary"
                block
                icon={
                  <SearchOutlined />
                }
                onClick={
                  refreshReports
                }
              >
                Aplicar
              </Button>

              <Button
                icon={
                  <ClearOutlined />
                }
                onClick={
                  clearFilters
                }
              />
            </Space>
          </Col>
        </Row>
      </Card>

      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col
          xs={24}
          sm={12}
          xl={6}
        >
          <Card>
            <Statistic
              title="Leads"
              value={
                summary.totalLeads
              }
              loading={
                loadingSummary
              }
              prefix={
                <UserAddOutlined />
              }
            />
          </Card>
        </Col>

        <Col
          xs={24}
          sm={12}
          xl={6}
        >
          <Card>
            <Statistic
              title="Participantes"
              value={
                summary.totalParticipants
              }
              loading={
                loadingSummary
              }
              prefix={
                <TeamOutlined />
              }
            />
          </Card>
        </Col>

        <Col
          xs={24}
          sm={12}
          xl={6}
        >
          <Card>
            <Statistic
              title="Jogadas"
              value={
                summary.totalSpins
              }
              loading={
                loadingSummary
              }
              prefix={
                <TrophyOutlined />
              }
            />
          </Card>
        </Col>

        <Col
          xs={24}
          sm={12}
          xl={6}
        >
          <Card>
            <Statistic
              title="Conversão"
              value={
                summary.conversionRate
              }
              precision={2}
              suffix="%"
              loading={
                loadingSummary
              }
              prefix={
                <BarChartOutlined />
              }
            />
          </Card>
        </Col>

        <Col
          xs={24}
          sm={12}
          xl={6}
        >
          <Card>
            <Statistic
              title="Brindes sorteados"
              value={
                summary.wonSpins
              }
              loading={
                loadingSummary
              }
              prefix={
                <GiftOutlined />
              }
            />
          </Card>
        </Col>

        <Col
          xs={24}
          sm={12}
          xl={6}
        >
          <Card>
            <Statistic
              title="Entregues"
              value={
                summary.deliveries
                  .delivered
              }
              loading={
                loadingSummary
              }
              prefix={
                <CheckCircleOutlined />
              }
            />
          </Card>
        </Col>

        <Col
          xs={24}
          sm={12}
          xl={6}
        >
          <Card>
            <Statistic
              title="Pendentes"
              value={
                summary.deliveries
                  .pending
              }
              loading={
                loadingSummary
              }
            />
          </Card>
        </Col>

        <Col
          xs={24}
          sm={12}
          xl={6}
        >
          <Card>
            <Statistic
              title="Estoque disponível"
              value={
                summary.stock
                  .available
              }
              suffix={`/ ${
                summary.stock
                  .initial || 0
              }`}
              loading={
                loadingSummary
              }
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Relatório de Leads"
        extra={
          <Space wrap>
            <Button
              icon={
                <FileTextOutlined />
              }
              loading={
                exporting === 'csv'
              }
              onClick={() =>
                handleExport('csv')
              }
            >
              CSV
            </Button>

            <Button
              icon={
                <FileExcelOutlined />
              }
              loading={
                exporting === 'xlsx'
              }
              onClick={() =>
                handleExport('xlsx')
              }
            >
              Excel
            </Button>

            <Button
              icon={
                <FilePdfOutlined />
              }
              loading={
                exporting === 'pdf'
              }
              onClick={() =>
                handleExport('pdf')
              }
            >
              PDF
            </Button>

            <Button
              icon={
                <DownloadOutlined />
              }
              onClick={
                refreshReports
              }
            >
              Recarregar
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{
            marginBottom: 16,
          }}
          message="Os campos personalizados do formulário são adicionados automaticamente à tabela e ao arquivo Excel."
        />

        <Table
          rowKey="id"
          loading={loadingLeads}
          columns={tableColumns}
          dataSource={leads}
          pagination={{
            current:
              pagination.current,

            pageSize:
              pagination.pageSize,

            total:
              pagination.total,

            showSizeChanger:
              true,

            pageSizeOptions: [
              '10',
              '20',
              '50',
              '100',
            ],

            showTotal: (
              total,
            ) =>
              `${total} leads encontrados`,
          }}
          onChange={
            handleTableChange
          }
          scroll={{
            x:
              1700 +
              customColumns.length *
                180,
          }}
          locale={{
            emptyText:
              'Nenhum lead encontrado para os filtros selecionados.',
          }}
        />
      </Card>

      <Drawer
        title="Dados do Lead"
        visible={detailOpen}
        width={620}
        onClose={() => {
          setDetailOpen(false)
          setSelectedLead(null)
        }}
      >
        {selectedLead && (
          <>
            <Descriptions
              bordered
              column={1}
              size="small"
            >
              <Descriptions.Item label="Data">
                {formatDateTime(
                  selectedLead.createdAt,
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Cliente">
                {selectedLead.clientName ||
                  '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Evento">
                {selectedLead.eventName ||
                  '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Jogo">
                {selectedLead.gameName ||
                  '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Formulário">
                {selectedLead.formName ||
                  '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Nome">
                {selectedLead.nome ||
                  '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Telefone">
                {selectedLead.telefone ||
                  '-'}
              </Descriptions.Item>

              <Descriptions.Item label="E-mail">
                {selectedLead.email ||
                  '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Consentimento LGPD">
                {selectedLead.consentLgpd
                  ? 'Sim'
                  : 'Não'}
              </Descriptions.Item>

              <Descriptions.Item label="IP">
                {selectedLead.ip ||
                  '-'}
              </Descriptions.Item>
            </Descriptions>

            {Object.keys(
              selectedLead.customValues ||
                {},
            ).length > 0 && (
              <>
                <Title
                  level={4}
                  style={{
                    marginTop: 24,
                  }}
                >
                  Campos personalizados
                </Title>

                <Descriptions
                  bordered
                  column={1}
                  size="small"
                >
                  {Object.entries(
                    selectedLead.customValues,
                  ).map(
                    ([
                      key,
                      value,
                    ]) => (
                      <Descriptions.Item
                        key={key}
                        label={key}
                      >
                        {value || '-'}
                      </Descriptions.Item>
                    ),
                  )}
                </Descriptions>
              </>
            )}
          </>
        )}
      </Drawer>
    </>
  )
}

export default ReportsPage